import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Calendar, Clock, ChevronRight, ListOrdered, Calculator, FileText } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { SEOHead, breadcrumbSchema, faqSchema, organizationSchema } from '../components/SEOHead';
import { BlogMarkdown, extractHeadings } from '../components/BlogMarkdown';
import { BlogCard, coverSrc, formatBlogDate } from '../components/BlogCard';
import { ShareButtons } from './ConseilPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const SITE_URL = 'https://ptitservices.com';
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_easers-pro/artifacts/j38s08lp_LOGOSITE.png';
const fadeUp = { hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0, transition: { duration: 0.4 } } };

export default function BlogPostPage() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true); setNotFound(false);
    axios.get(`${BACKEND_URL}/api/blog/${slug}`)
      .then((res) => { setItem(res.data.item); setRelated(res.data.related || []); })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const headings = useMemo(() => extractHeadings(item?.content_md), [item]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 space-y-4" data-testid="blog-post-loading">
        <Skeleton className="h-5 w-48" /><Skeleton className="h-12 w-full" /><Skeleton className="h-4 w-2/3" /><Skeleton className="aspect-[16/9] w-full rounded-2xl" /><Skeleton className="h-4 w-full" /><Skeleton className="h-4 w-5/6" />
      </div>
    );
  }

  if (notFound || !item) {
    return (
      <div className="max-w-lg mx-auto px-4 py-20 text-center" data-testid="blog-post-not-found">
        <SEOHead title="Article introuvable" noindex path={`/blog/${slug}`} />
        <h1 className="font-['Space_Grotesk'] text-2xl font-semibold mb-3">Article introuvable</h1>
        <p className="text-muted-foreground mb-6">Cet article n'existe plus ou l'adresse est incorrecte.</p>
        <Link to="/blog"><Button><ArrowLeft className="mr-2 h-4 w-4" />Retour au blog</Button></Link>
      </div>
    );
  }

  const path = `/blog/${item.slug}`;
  const cover = coverSrc(item.cover_url);
  const articleSchema = {
    '@context': 'https://schema.org', '@type': 'BlogPosting',
    'headline': item.title, 'description': item.excerpt, 'inLanguage': 'fr-FR',
    'datePublished': item.published_at, 'dateModified': item.updated_at || item.published_at,
    'keywords': (item.tags || []).join(', '), 'articleSection': item.category_label, 'wordCount': item.word_count,
    'author': organizationSchema(), 'publisher': organizationSchema(),
    'mainEntityOfPage': { '@type': 'WebPage', '@id': `${SITE_URL}${path}` },
    'image': item.cover_url ? `${SITE_URL}${item.cover_url}` : LOGO_URL,
  };
  const schemas = [articleSchema, breadcrumbSchema([{ name: 'Accueil', path: '/' }, { name: 'Blog', path: '/blog' }, { name: item.title, path }])];
  if (item.faq?.length) schemas.push(faqSchema(item.faq.map((f) => ({ q: f.question, a: f.reponse }))));

  return (
    <article data-testid="blog-post-page">
      <SEOHead title={item.title} description={item.excerpt} path={path} type="article" image={item.cover_url ? `${SITE_URL}${item.cover_url}` : undefined} schema={schemas} />

      {/* Header */}
      <header className="bg-[hsl(var(--secondary))]/60">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-8 pb-10 sm:pt-12 sm:pb-14">
          <nav aria-label="Fil d'Ariane" className="flex items-center gap-1.5 text-xs text-muted-foreground mb-6" data-testid="blog-breadcrumb">
            <Link to="/" className="hover:text-foreground">Accueil</Link><ChevronRight className="h-3 w-3" />
            <Link to="/blog" className="hover:text-foreground">Blog</Link><ChevronRight className="h-3 w-3" />
            <Link to={`/blog?categorie=${item.category}`} className="hover:text-foreground">{item.category_label}</Link>
          </nav>
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <Badge className="mb-4 rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90" data-testid="blog-post-category">{item.category_label}</Badge>
            <h1 className="font-['Space_Grotesk'] text-3xl sm:text-4xl font-semibold tracking-tight leading-tight mb-4" data-testid="blog-post-title">{item.title}</h1>
            <p className="text-lg text-muted-foreground leading-relaxed mb-5" data-testid="blog-post-excerpt">{item.excerpt}</p>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1.5"><Calendar className="h-4 w-4" /><time dateTime={item.published_at}>{formatBlogDate(item.published_at)}</time></span>
              <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4" /> {item.reading_time_min} min de lecture</span>
              <span>Par <strong className="text-foreground font-medium">l'équipe Ptitservices</strong></span>
            </div>
          </motion.div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        {cover && (
          <motion.figure initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }} className="-mt-6 sm:-mt-8 mb-8 rounded-2xl overflow-hidden shadow-md border border-border bg-card" data-testid="blog-post-cover">
            <img src={cover} alt={item.title} className="w-full aspect-[16/9] object-cover" />
          </motion.figure>
        )}

        {/* Table of contents */}
        {headings.length >= 3 && (
          <Card className="p-5 mb-8 bg-[hsl(var(--brand-mint))]/30 border-[hsl(var(--primary))]/20" data-testid="blog-post-toc">
            <p className="text-sm font-semibold flex items-center gap-2 mb-3"><ListOrdered className="h-4 w-4 text-[hsl(var(--primary))]" /> Sommaire</p>
            <ol className="space-y-1.5 text-sm">
              {headings.map((h, i) => <li key={h.id}><a href={`#${h.id}`} className="text-foreground/80 hover:text-[hsl(var(--primary))]"><span className="text-[hsl(var(--primary))] font-medium mr-2">{i + 1}.</span>{h.text}</a></li>)}
            </ol>
          </Card>
        )}

        <BlogMarkdown content={item.content_md} />

        {/* FAQ */}
        {item.faq?.length > 0 && (
          <section className="mt-12" data-testid="blog-post-faq">
            <h2 className="font-['Space_Grotesk'] text-2xl font-semibold tracking-tight mb-4">Questions fréquentes</h2>
            <Accordion type="single" collapsible className="space-y-3">
              {item.faq.map((f, i) => (
                <AccordionItem key={f.question} value={`faq-${i}`} className="border border-border rounded-lg px-4 bg-card">
                  <AccordionTrigger className="text-sm font-medium text-left hover:no-underline">{f.question}</AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground leading-relaxed">{f.reponse}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>
        )}

        {/* Tags + share */}
        <div className="mt-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-t border-border pt-6">
          <div className="flex flex-wrap gap-1.5" data-testid="blog-post-tags">{(item.tags || []).map((t) => <Badge key={t} variant="secondary" className="font-normal">#{t}</Badge>)}</div>
          <ShareButtons url={path} title={item.title} />
        </div>

        {/* CTA */}
        <Card className="mt-10 p-6 sm:p-8 bg-[hsl(var(--primary))] text-primary-foreground border-0" data-testid="blog-post-cta">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-5">
            <div>
              <h2 className="font-['Space_Grotesk'] text-xl sm:text-2xl font-semibold mb-1">Et si un Easer s'en occupait pour vous ?</h2>
              <p className="text-primary-foreground/85 text-sm">Ménage, jardinage, repassage… avec 50 % de crédit d'impôt déduit immédiatement. Devis gratuit, sans engagement.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 shrink-0">
              <Link to="/devis"><Button variant="secondary" className="w-full"><FileText className="mr-2 h-4 w-4" /> Devis gratuit</Button></Link>
              <Link to="/credit-impot"><Button variant="outline" className="w-full border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white"><Calculator className="mr-2 h-4 w-4" /> Simuler mon coût</Button></Link>
            </div>
          </div>
        </Card>
      </div>

      {/* Related */}
      {related.length > 0 && (
        <section className="py-14 mt-14 bg-[hsl(var(--secondary))]/60" data-testid="blog-post-related">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-['Space_Grotesk'] text-2xl font-semibold tracking-tight">À lire aussi</h2>
              <Link to="/blog" className="text-sm font-medium text-[hsl(var(--primary))] inline-flex items-center gap-1 hover:underline">Tous les articles <ArrowRight className="h-4 w-4" /></Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{related.map((p) => <BlogCard key={p.id} post={p} compact />)}</div>
          </div>
        </section>
      )}
    </article>
  );
}
